/**
 * ATLAS Debug Service
 *
 * Service for debugging BEO ATLAS API submissions
 */

import api from '../shared/services/api';

export interface AtlasDebugFile {
  filename: string;
  timestamp: string;
  type: 'original' | 'cleaned' | 'response' | 'error';
  size: number;
}

export interface AtlasDebugAnalysis {
  valid: boolean;
  missing_required_fields: string[];
  invalid_fields: string[];
  misplaced_fields: Array<{
    field: string;
    current_location: string;
    correct_location: string;
  }>;
  date_issues: Array<{
    field: string;
    value: any;
    issue: string;
  }>;
  structure_issues: string[];
  recommendations: string[];
}

export interface AtlasTestResponse {
  success: boolean;
  status_code: number;
  response?: any;
  error?: string;
  error_details?: any;
  request_saved_as?: string;
  response_saved_as?: string;
  auth_error?: boolean;
  auth_message?: string;
}

class AtlasDebugService {
  /**
   * Check authentication status for BEO ATLAS
   */
  async checkAuthStatus(): Promise<{ authenticated: boolean; module_id?: string; message: string }> {
    const response = await api.get('/api/v1/admin/atlas-debug/auth-status');
    return response.data;
  }

  /**
   * Logout from BEO ATLAS (clear OAuth token)
   */
  async logout(): Promise<{ success: boolean; message: string }> {
    const response = await api.delete('/api/v1/admin/atlas-debug/logout');
    return response.data;
  }

  /**
   * Get list of all debug files
   */
  async getDebugFiles(): Promise<AtlasDebugFile[]> {
    const response = await api.get('/api/v1/admin/atlas-debug/files');
    return response.data.files;
  }

  /**
   * Get content of a specific debug file
   */
  async getFileContent(filename: string): Promise<any> {
    const response = await api.get(`/api/v1/admin/atlas-debug/file/${filename}`);
    return response.data;
  }

  /**
   * Test ATLAS submission with custom payload
   */
  async testSubmission(payload: any): Promise<AtlasTestResponse> {
    const response = await api.post('/api/v1/admin/atlas-debug/test', { payload });
    return response.data;
  }

  /**
   * Clear all debug files
   */
  async clearDebugFiles(): Promise<{ message: string; deleted_count: number }> {
    const response = await api.delete('/api/v1/admin/atlas-debug/files');
    return response.data;
  }

  /**
   * Analyze ATLAS structure for issues
   */
  async analyzeStructure(payload: any): Promise<AtlasDebugAnalysis> {
    const response = await api.post('/api/v1/admin/atlas-debug/analyze', { data: payload });
    return response.data;
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(filename: string): string {
    // Extract timestamp from filename (format: YYYY-MM-DD_HHMMSS_*.json)
    const match = filename.match(/(\d{4}-\d{2}-\d{2})_(\d{6})/);
    if (match) {
      const date = match[1];
      const time = match[2];
      const formattedTime = `${time.slice(0, 2)}:${time.slice(2, 4)}:${time.slice(4, 6)}`;
      return `${date} ${formattedTime}`;
    }
    return filename;
  }

  /**
   * Get file type from filename
   */
  getFileType(filename: string): 'original' | 'cleaned' | 'response' | 'error' {
    if (filename.includes('_original')) return 'original';
    if (filename.includes('_cleaned')) return 'cleaned';
    if (filename.includes('_response')) return 'response';
    if (filename.includes('_error')) return 'error';
    return 'original';
  }

  /**
   * Format file size for display
   */
  formatFileSize(size: number): string {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
}

export default new AtlasDebugService();